import { type BadgeProps } from '@radix-ui/themes'
import { formatInTimeZone } from 'date-fns-tz'
import { sortBy } from 'lodash'
import {
  PrizeRevealMethod,
  RewardTypeSummary,
  RewardScheduleStatus,
  RewardScheduleWithGame,
  S2WGame,
  S2WTheme,
} from '@Generated/back-office-client/src'

export const PRIZE_SCHEDULES_QUERY_KEY = ['prize-schedules']

export type GameSchedules = {
  game: S2WGame
  schedules: RewardScheduleWithGame[]
}

// Every game in the API's order (by name), so a game with no current schedule can still be edited. The API orders
// schedules by start date within a game; keep that order within each group.
export const groupSchedulesByGame = (
  games: S2WGame[],
  schedules: RewardScheduleWithGame[],
): GameSchedules[] =>
  games.map((game) => ({
    game,
    schedules: schedules.filter((schedule) => schedule.s2wGameId === game.id),
  }))

// The themes the server accepts, without the generated client's unknown-value fallback
export const S2W_THEMES: S2WTheme[] = Object.values(S2WTheme).filter(
  (theme) => theme !== S2WTheme.unknown_default_open_api,
)

// Mirrors GDS's validateThemeAndRevealMethod, which stays the authority: these themes have no group-wheel art, so
// the S2W client can only run them with a single wheel
const SINGLE_WHEEL_ONLY_THEMES: S2WTheme[] = [S2WTheme.MICHIGAN_THEME_01]

export const supportsDoubleWheel = (theme: S2WTheme | ''): boolean =>
  !SINGLE_WHEEL_ONLY_THEMES.some((singleWheelTheme) => singleWheelTheme === theme)

export const PRIZE_REVEAL_METHODS: PrizeRevealMethod[] = [
  PrizeRevealMethod.DOUBLE_WHEEL_PRIZE_REVEAL,
  PrizeRevealMethod.SINGLE_WHEEL_PRIZE_REVEAL,
]

export const scheduleStatusLabel = (status: RewardScheduleStatus): string => {
  if (status === RewardScheduleStatus.ACTIVE) {
    return 'Active'
  }
  if (status === RewardScheduleStatus.UPCOMING) {
    return 'Scheduled'
  }
  if (status === RewardScheduleStatus.NOT_READY) {
    return 'Not ready'
  }
  return 'Unknown'
}

export const scheduleStatusColor = (status: RewardScheduleStatus): BadgeProps['color'] => {
  if (status === RewardScheduleStatus.ACTIVE) {
    return 'green'
  }
  if (status === RewardScheduleStatus.UPCOMING) {
    return 'blue'
  }
  if (status === RewardScheduleStatus.NOT_READY) {
    return 'orange'
  }
  return 'gray'
}

export const formatScheduleDates = (schedule: RewardScheduleWithGame, timezone: string): string =>
  `${formatInTimeZone(schedule.startDate, timezone, 'PP p')} – ${formatInTimeZone(schedule.endDate, timezone, 'PP p')}`

// Grouped by category, then alphabetical (case-insensitive) within each category
export const sortPrizes = (rewards: RewardTypeSummary[]): RewardTypeSummary[] =>
  sortBy(rewards, [(reward) => reward.rewardCategory, (reward) => reward.rewardName.toLowerCase()])

// Only a schedule that has started can be ended early; the server enforces the same rule
export const hasStarted = (schedule: RewardScheduleWithGame, now: Date): boolean =>
  schedule.startDate <= now

export const formatDailySpinLimit = (game: S2WGame): string => {
  if (!game.dailySpinLimit) {
    return 'Unlimited'
  }
  return `${game.dailySpinLimit} per day`
}

export const prizeRevealMethodLabel = (method?: PrizeRevealMethod | null): string => {
  if (method === PrizeRevealMethod.SINGLE_WHEEL_PRIZE_REVEAL) {
    return 'Single wheel'
  }
  if (method === PrizeRevealMethod.DOUBLE_WHEEL_PRIZE_REVEAL) {
    return 'Double wheel'
  }
  return '-'
}

export const formatPrizeRevealMethod = (game: S2WGame): string =>
  prizeRevealMethodLabel(game.prizeRevealMethod)
